import type { ResultadoBusqueda } from '../services/tariff-catalog-service';

/** Un renglón de la vista por niveles: o un título del libro, o una partida. */
export type ItemNivelado =
  | { tipo: 'nivel'; texto: string; nivel: number; code: string | null }
  | { tipo: 'fila'; fila: ResultadoBusqueda; nivel: number };

const SEP = ' > ';

/**
 * Los niveles del libro dentro de UN grupo de partida (frente C1 del plan 178).
 *
 * El arancel oficial organiza cada partida con guiones (`-`, `- -`, `- - -`) y
 * los usuarios leen así. Los niveles intermedios («Con la parte superior de
 * cuero natural o regenerado:») NO existen como filas: viven dentro del
 * `fullContext` de las hojas y acá se reconstruyen — el mismo criterio que
 * `construirArbol` en el backend (`tariff-tree-builder.service.ts`), aplanado
 * para renderizar como renglones.
 *
 * ⚖️ Dentro del grupo se ordena POR CÓDIGO, como se lee el libro. El ranking ya
 * decidió qué capítulos y partidas van primero y cuál fila lleva la estrella;
 * entre hermanas de la misma partida el orden del libro explica más que el del
 * consenso (dos «Los demás» seguidas solo se distinguen por su nivel).
 */
export function aplanarNiveles(filas: ResultadoBusqueda[]): ItemNivelado[] {
  const ordenadas = [...filas].sort((a, b) => a.code.localeCompare(b.code));
  const items: ItemNivelado[] = [];
  let rutaPrevia: string[] = [];

  for (const fila of ordenadas) {
    const segmentos = (fila.fullContext ?? fila.description)
      .split(SEP)
      .map((s) => s.trim())
      .filter(Boolean);
    // El tramo 0 es la raíz de 4 dígitos (ya es el encabezado del grupo) y el
    // último es la propia fila. Lo del medio son los niveles del libro.
    const intermedios = segmentos.slice(1, -1);

    // Igual que `construirArbol` en el backend: `parentCodes` trae la raíz
    // primero y después los intermedios MÁS PROFUNDOS, así que se alinean
    // desde el fondo (verificado con 6405.10.00.10: `64.05 > 6405.10.00`).
    const codigosIntermedios = (fila.parentCodes ?? '')
      .split(SEP)
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(1);
    const codigoDeNivel = (i: number): string | null => {
      const idx = codigosIntermedios.length - (intermedios.length - i);
      return idx >= 0 ? (codigosIntermedios[idx] ?? null) : null;
    };

    let distinto = false;
    intermedios.forEach((texto, i) => {
      // Un nivel que cambió invalida todo lo que cuelga debajo: aunque el
      // texto de un subnivel coincida por casualidad («Los demás:»), pertenece
      // a otra rama y se vuelve a emitir.
      if (distinto || rutaPrevia[i] !== texto) {
        distinto = true;
        items.push({ tipo: 'nivel', texto, nivel: i + 1, code: codigoDeNivel(i) });
      }
    });
    rutaPrevia = intermedios;

    items.push({ tipo: 'fila', fila, nivel: Math.max(intermedios.length + 1, 1) });
  }

  return items;
}
